import React, { useEffect, useMemo, useState } from 'react'
import { Edit, Plus, Search, Trash2, UserPlus, Users, X } from 'lucide-react'
import { toast } from 'react-hot-toast'
import api from '../../../services/authService'
import { obtenerMensajeErrorApi } from '../../../services/apiError'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'

interface MiembroEmpresa {
  id_miembro: number
  id_usuario: number
  nombre: string
  apellido?: string | null
  email: string
  rol_global: string
  rol_miembro: 'admin' | 'moderador' | 'supervisor' | 'contador' | 'usuario'
  estado: 'activo' | 'inactivo' | 'suspendido'
  turno?: string | null
  departamento?: string | null
  cargo?: string | null
  telefono?: string | null
}

interface GrupoEmpresa {
  id_grupo: number
  nombre: string
  descripcion?: string | null
  tipo_grupo?: string | null
  activo: boolean
  miembros_count: number
}

interface MiembroGrupo {
  id_grupo_miembro: number
  id_miembro: number
  id_usuario: number
  nombre: string
  apellido?: string | null
  email: string
  rol_miembro: string
  departamento?: string | null
  turno?: string | null
}

interface DashboardGestionEmpresa {
  vista: 'gestion'
  organizacion: {
    id_organizacion: number
    nombre: string
    tipo: string
    subtipo?: string | null
  }
  metricas: {
    total_miembros: number
    miembros_activos: number
    total_grupos: number
    total_equipos: number
    total_usuarios_operativos: number
    total_supervisores: number
    total_contadores: number
  }
  grupos: GrupoEmpresa[]
  miembros: MiembroEmpresa[]
}

const rolesDisponibles = [
  { value: 'admin', label: 'Admin' },
  { value: 'moderador', label: 'Moderador' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'contador', label: 'Contador' },
  { value: 'usuario', label: 'Usuario' },
] as const

const tiposGrupo = [
  { value: 'equipo', label: 'Equipo' },
  { value: 'departamento', label: 'Departamento' },
  { value: 'area', label: 'Area' },
  { value: 'sucursal', label: 'Sucursal' },
] as const

const PaginaGestionEmpresa: React.FC = () => {
  const { usuario } = useAuth()
  const [resumen, setResumen] = useState<DashboardGestionEmpresa | null>(null)
  const [miembros, setMiembros] = useState<MiembroEmpresa[]>([])
  const [grupos, setGrupos] = useState<GrupoEmpresa[]>([])
  const [busqueda, setBusqueda] = useState('')
  const [filtroRol, setFiltroRol] = useState<string>('todos')
  const [loading, setLoading] = useState(false)
  const [credencialTemporal, setCredencialTemporal] = useState<{ correo: string; password: string } | null>(null)

  const [mostrarModalMiembro, setMostrarModalMiembro] = useState(false)
  const [mostrarModalEditarMiembro, setMostrarModalEditarMiembro] = useState(false)
  const [mostrarModalGrupo, setMostrarModalGrupo] = useState(false)
  const [mostrarModalEditarGrupo, setMostrarModalEditarGrupo] = useState(false)
  const [mostrarModalGestionGrupo, setMostrarModalGestionGrupo] = useState(false)

  const [nuevoMiembro, setNuevoMiembro] = useState({
    nombre: '',
    apellido: '',
    correo: '',
    telefono: '',
    cargo: '',
    rol_miembro: 'usuario',
    departamento: '',
    turno: '',
    id_grupo: '',
  })
  const [miembroAEditar, setMiembroAEditar] = useState<MiembroEmpresa | null>(null)

  const [nuevoGrupo, setNuevoGrupo] = useState({
    nombre: '',
    descripcion: '',
    tipo_grupo: 'equipo',
  })
  const [grupoAEditar, setGrupoAEditar] = useState<GrupoEmpresa | null>(null)
  const [grupoSeleccionado, setGrupoSeleccionado] = useState<GrupoEmpresa | null>(null)
  const [miembrosGrupo, setMiembrosGrupo] = useState<MiembroGrupo[]>([])
  const [miembroAAgregar, setMiembroAAgregar] = useState('')
  const [busquedaGrupo, setBusquedaGrupo] = useState('')

  const cargarDatos = async () => {
    if (typeof usuario?.id_organizacion !== 'number') {
      setResumen(null)
      setMiembros([])
      setGrupos([])
      toast.error('No se encontró una organización activa para la gestión empresarial')
      return
    }

    setLoading(true)
    try {
      const respuesta = await api.get<DashboardGestionEmpresa>('/api/organizaciones/mi-dashboard')
      setResumen(respuesta.data)
      setMiembros(respuesta.data.miembros || [])
      setGrupos(respuesta.data.grupos || [])
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo cargar la gestión empresarial'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    cargarDatos()
  }, [usuario?.id_organizacion])

  const miembrosFiltrados = useMemo(() => {
    return miembros.filter((miembro) => {
      const cumpleBusqueda =
        `${miembro.nombre} ${miembro.apellido || ''}`.toLowerCase().includes(busqueda.toLowerCase()) ||
        miembro.email.toLowerCase().includes(busqueda.toLowerCase()) ||
        (miembro.departamento || '').toLowerCase().includes(busqueda.toLowerCase())

      if (!cumpleBusqueda) return false
      if (filtroRol === 'todos') return true
      return miembro.rol_miembro === filtroRol
    })
  }, [busqueda, filtroRol, miembros])

  const miembrosDisponiblesParaGrupo = useMemo(() => {
    const idsActuales = new Set(miembrosGrupo.map((item) => item.id_miembro))
    return miembros.filter((miembro) => {
      if (idsActuales.has(miembro.id_miembro)) return false
      if (!busquedaGrupo.trim()) return true
      const texto = `${miembro.nombre} ${miembro.apellido || ''} ${miembro.email}`.toLowerCase()
      return texto.includes(busquedaGrupo.toLowerCase())
    })
  }, [miembros, miembrosGrupo, busquedaGrupo])

  const crearMiembro = async () => {
    if (!nuevoMiembro.nombre.trim() || !nuevoMiembro.correo.trim()) {
      toast.error('Nombre y correo son obligatorios')
      return
    }

    try {
      const respuesta = await api.post('/api/organizaciones/miembros', {
        ...nuevoMiembro,
        nombre: nuevoMiembro.nombre.trim(),
        apellido: nuevoMiembro.apellido.trim() || undefined,
        correo: nuevoMiembro.correo.trim(),
        telefono: nuevoMiembro.telefono.trim() || undefined,
        cargo: nuevoMiembro.cargo.trim() || undefined,
        departamento: nuevoMiembro.departamento.trim() || undefined,
        turno: nuevoMiembro.turno.trim() || undefined,
        id_grupo: nuevoMiembro.id_grupo ? parseInt(nuevoMiembro.id_grupo, 10) : undefined,
      })
      setCredencialTemporal({
        correo: respuesta.data.miembro.email,
        password: respuesta.data.password_temporal,
      })
      setMostrarModalMiembro(false)
      setNuevoMiembro({
        nombre: '',
        apellido: '',
        correo: '',
        telefono: '',
        cargo: '',
        rol_miembro: 'usuario',
        departamento: '',
        turno: '',
        id_grupo: '',
      })
      toast.success('Miembro creado correctamente')
      await cargarDatos()
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo crear el miembro'))
    }
  }

  const guardarEdicionMiembro = async () => {
    if (!miembroAEditar) return
    try {
      await api.put(`/api/organizaciones/miembros/${miembroAEditar.id_miembro}`, {
        rol_miembro: miembroAEditar.rol_miembro,
        estado: miembroAEditar.estado,
        departamento: miembroAEditar.departamento || undefined,
        turno: miembroAEditar.turno || undefined,
        cargo: miembroAEditar.cargo || undefined,
        telefono: miembroAEditar.telefono || undefined,
        activo: miembroAEditar.estado === 'activo',
      })
      toast.success('Miembro actualizado correctamente')
      setMostrarModalEditarMiembro(false)
      setMiembroAEditar(null)
      await cargarDatos()
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo actualizar el miembro'))
    }
  }

  const eliminarMiembro = async (id_miembro: number) => {
    if (!window.confirm('¿Deseas desvincular este miembro de la empresa?')) return
    try {
      await api.delete(`/api/organizaciones/miembros/${id_miembro}`)
      toast.success('Miembro desvinculado correctamente')
      await cargarDatos()
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo eliminar el miembro'))
    }
  }

  const crearGrupo = async () => {
    if (!nuevoGrupo.nombre.trim()) {
      toast.error('El nombre del grupo es obligatorio')
      return
    }
    try {
      await api.post('/api/organizaciones/grupos', {
        nombre: nuevoGrupo.nombre.trim(),
        descripcion: nuevoGrupo.descripcion.trim() || undefined,
        tipo_grupo: nuevoGrupo.tipo_grupo,
      })
      toast.success('Grupo creado correctamente')
      setNuevoGrupo({ nombre: '', descripcion: '', tipo_grupo: 'equipo' })
      setMostrarModalGrupo(false)
      await cargarDatos()
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo crear el grupo'))
    }
  }

  const guardarEdicionGrupo = async () => {
    if (!grupoAEditar) return
    try {
      await api.put(`/api/organizaciones/grupos/${grupoAEditar.id_grupo}`, {
        nombre: grupoAEditar.nombre,
        descripcion: grupoAEditar.descripcion || undefined,
        tipo_grupo: grupoAEditar.tipo_grupo || undefined,
      })
      toast.success('Grupo actualizado correctamente')
      setMostrarModalEditarGrupo(false)
      setGrupoAEditar(null)
      await cargarDatos()
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo actualizar el grupo'))
    }
  }

  const eliminarGrupo = async (id_grupo: number) => {
    if (!window.confirm('¿Deseas eliminar este grupo?')) return
    try {
      await api.delete(`/api/organizaciones/grupos/${id_grupo}`)
      toast.success('Grupo eliminado correctamente')
      await cargarDatos()
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo eliminar el grupo'))
    }
  }

  const abrirGestionGrupo = async (grupo: GrupoEmpresa) => {
    setGrupoSeleccionado(grupo)
    setMostrarModalGestionGrupo(true)
    try {
      const respuesta = await api.get<MiembroGrupo[]>(`/api/organizaciones/grupos/${grupo.id_grupo}/miembros`)
      setMiembrosGrupo(respuesta.data || [])
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudieron cargar los miembros del grupo'))
    }
  }

  const cerrarGestionGrupo = () => {
    setMostrarModalGestionGrupo(false)
    setGrupoSeleccionado(null)
    setMiembrosGrupo([])
    setMiembroAAgregar('')
    setBusquedaGrupo('')
  }

  const agregarMiembroAGrupo = async () => {
    if (!grupoSeleccionado || !miembroAAgregar) return
    try {
      await api.post(`/api/organizaciones/grupos/${grupoSeleccionado.id_grupo}/miembros`, {
        id_miembro: parseInt(miembroAAgregar, 10),
      })
      toast.success('Miembro asignado al grupo')
      const respuesta = await api.get<MiembroGrupo[]>(`/api/organizaciones/grupos/${grupoSeleccionado.id_grupo}/miembros`)
      setMiembrosGrupo(respuesta.data || [])
      setMiembroAAgregar('')
      await cargarDatos()
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo agregar el miembro al grupo'))
    }
  }

  const quitarMiembroDeGrupo = async (id_miembro: number) => {
    if (!grupoSeleccionado) return
    try {
      await api.delete(`/api/organizaciones/grupos/${grupoSeleccionado.id_grupo}/miembros/${id_miembro}`)
      toast.success('Miembro removido del grupo')
      const respuesta = await api.get<MiembroGrupo[]>(`/api/organizaciones/grupos/${grupoSeleccionado.id_grupo}/miembros`)
      setMiembrosGrupo(respuesta.data || [])
      await cargarDatos()
    } catch (error) {
      toast.error(obtenerMensajeErrorApi(error, 'No se pudo remover el miembro del grupo'))
    }
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestión Empresarial</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {resumen?.organizacion?.nombre
              ? `Administra miembros, equipos y roles de ${resumen.organizacion.nombre}`
              : 'Administra miembros, equipos y roles empresariales'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setMostrarModalGrupo(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <Plus className="h-4 w-4" />
            Nuevo equipo
          </button>
          <button
            onClick={() => setMostrarModalMiembro(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700"
          >
            <UserPlus className="h-4 w-4" />
            Nuevo miembro
          </button>
        </div>
      </div>

      {credencialTemporal && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold">Credencial temporal generada</p>
              <p className="mt-1">Correo: {credencialTemporal.correo}</p>
              <p>Contraseña temporal: {credencialTemporal.password}</p>
            </div>
            <button onClick={() => setCredencialTemporal(null)} aria-label="Cerrar credencial temporal" className="text-amber-700 dark:text-amber-200">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Miembros activos</p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{resumen?.metricas.miembros_activos ?? 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Equipos</p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{resumen?.metricas.total_equipos ?? 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Supervisores</p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{resumen?.metricas.total_supervisores ?? 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Contadores</p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{resumen?.metricas.total_contadores ?? 0}</p>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Miembros</h2>
              <div className="flex gap-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <label htmlFor="empresa-gestion-busqueda-miembro" className="sr-only">Buscar miembro</label>
                  <input
                    id="empresa-gestion-busqueda-miembro"
                    name="busquedaMiembro"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar miembro"
                    className="rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="empresa-gestion-filtro-rol" className="sr-only">Filtrar por rol</label>
                  <select
                    id="empresa-gestion-filtro-rol"
                    name="filtroRol"
                    value={filtroRol}
                    onChange={(e) => setFiltroRol(e.target.value)}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  >
                    <option value="todos">Todos</option>
                    {rolesDisponibles.map((rol) => (
                      <option key={rol.value} value={rol.value}>{rol.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Miembro</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Rol</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Departamento</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {miembrosFiltrados.map((miembro) => (
                  <tr key={miembro.id_miembro}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{`${miembro.nombre} ${miembro.apellido || ''}`.trim()}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{miembro.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{miembro.rol_miembro}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{miembro.departamento || 'Sin asignar'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${miembro.estado === 'activo' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                        {miembro.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setMiembroAEditar({ ...miembro })
                            setMostrarModalEditarMiembro(true)
                          }}
                          aria-label={`Editar miembro ${miembro.nombre} ${miembro.apellido || ''}`.trim()}
                          className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => eliminarMiembro(miembro.id_miembro)}
                          aria-label={`Eliminar miembro ${miembro.nombre} ${miembro.apellido || ''}`.trim()}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {miembrosFiltrados.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      {loading ? 'Cargando miembros...' : 'No hay miembros para mostrar.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Equipos y áreas</h2>
          </div>
          <div className="space-y-3 p-4">
            {grupos.map((grupo) => (
              <div key={grupo.id_grupo} className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{grupo.nombre}</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{grupo.descripcion || 'Sin descripción registrada'}</p>
                    <p className="mt-2 text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">{grupo.tipo_grupo || 'equipo'}</p>
                  </div>
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    {grupo.miembros_count} miembros
                  </span>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => abrirGestionGrupo(grupo)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    Gestionar
                  </button>
                  <button
                    onClick={() => {
                      setGrupoAEditar({ ...grupo })
                      setMostrarModalEditarGrupo(true)
                    }}
                    aria-label={`Editar grupo ${grupo.nombre}`}
                    className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => eliminarGrupo(grupo.id_grupo)}
                    aria-label={`Eliminar grupo ${grupo.nombre}`}
                    className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            {grupos.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                No hay grupos registrados.
              </div>
            )}
          </div>
        </div>
      </div>

      {mostrarModalMiembro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Nuevo miembro empresarial</h3>
              <button onClick={() => setMostrarModalMiembro(false)} aria-label="Cerrar modal de nuevo miembro" className="text-gray-500"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="empresa-nuevo-miembro-nombre" className="sr-only">Nombre</label>
                <input id="empresa-nuevo-miembro-nombre" name="nuevoMiembro.nombre" value={nuevoMiembro.nombre} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, nombre: e.target.value })} placeholder="Nombre" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-miembro-apellido" className="sr-only">Apellido</label>
                <input id="empresa-nuevo-miembro-apellido" name="nuevoMiembro.apellido" value={nuevoMiembro.apellido} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, apellido: e.target.value })} placeholder="Apellido" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-miembro-correo" className="sr-only">Correo</label>
                <input id="empresa-nuevo-miembro-correo" name="nuevoMiembro.correo" type="email" value={nuevoMiembro.correo} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, correo: e.target.value })} placeholder="Correo" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-miembro-telefono" className="sr-only">Telefono</label>
                <input id="empresa-nuevo-miembro-telefono" name="nuevoMiembro.telefono" value={nuevoMiembro.telefono} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, telefono: e.target.value })} placeholder="Telefono" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-miembro-cargo" className="sr-only">Cargo</label>
                <input id="empresa-nuevo-miembro-cargo" name="nuevoMiembro.cargo" value={nuevoMiembro.cargo} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, cargo: e.target.value })} placeholder="Cargo" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-miembro-departamento" className="sr-only">Departamento</label>
                <input id="empresa-nuevo-miembro-departamento" name="nuevoMiembro.departamento" value={nuevoMiembro.departamento} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, departamento: e.target.value })} placeholder="Departamento" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-miembro-turno" className="sr-only">Turno</label>
                <input id="empresa-nuevo-miembro-turno" name="nuevoMiembro.turno" value={nuevoMiembro.turno} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, turno: e.target.value })} placeholder="Turno" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-miembro-rol" className="sr-only">Rol</label>
                <select id="empresa-nuevo-miembro-rol" name="nuevoMiembro.rol_miembro" value={nuevoMiembro.rol_miembro} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, rol_miembro: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                  {rolesDisponibles.map((rol) => <option key={rol.value} value={rol.value}>{rol.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="empresa-nuevo-miembro-grupo" className="sr-only">Grupo inicial</label>
                <select id="empresa-nuevo-miembro-grupo" name="nuevoMiembro.id_grupo" value={nuevoMiembro.id_grupo} onChange={(e) => setNuevoMiembro({ ...nuevoMiembro, id_grupo: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2 w-full dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                  <option value="">Sin grupo inicial</option>
                  {grupos.map((grupo) => <option key={grupo.id_grupo} value={grupo.id_grupo}>{grupo.nombre}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setMostrarModalMiembro(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-600 dark:text-white">Cancelar</button>
              <button onClick={crearMiembro} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">Crear miembro</button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalEditarMiembro && miembroAEditar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Editar miembro</h3>
              <button onClick={() => setMostrarModalEditarMiembro(false)} aria-label="Cerrar modal de edición de miembro" className="text-gray-500"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="empresa-editar-miembro-telefono" className="sr-only">Telefono</label>
                <input id="empresa-editar-miembro-telefono" name="miembroAEditar.telefono" value={miembroAEditar.telefono || ''} onChange={(e) => setMiembroAEditar({ ...miembroAEditar, telefono: e.target.value })} placeholder="Telefono" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-editar-miembro-cargo" className="sr-only">Cargo</label>
                <input id="empresa-editar-miembro-cargo" name="miembroAEditar.cargo" value={miembroAEditar.cargo || ''} onChange={(e) => setMiembroAEditar({ ...miembroAEditar, cargo: e.target.value })} placeholder="Cargo" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-editar-miembro-departamento" className="sr-only">Departamento</label>
                <input id="empresa-editar-miembro-departamento" name="miembroAEditar.departamento" value={miembroAEditar.departamento || ''} onChange={(e) => setMiembroAEditar({ ...miembroAEditar, departamento: e.target.value })} placeholder="Departamento" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-editar-miembro-turno" className="sr-only">Turno</label>
                <input id="empresa-editar-miembro-turno" name="miembroAEditar.turno" value={miembroAEditar.turno || ''} onChange={(e) => setMiembroAEditar({ ...miembroAEditar, turno: e.target.value })} placeholder="Turno" className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-editar-miembro-rol" className="sr-only">Rol</label>
                <select id="empresa-editar-miembro-rol" name="miembroAEditar.rol_miembro" value={miembroAEditar.rol_miembro} onChange={(e) => setMiembroAEditar({ ...miembroAEditar, rol_miembro: e.target.value as MiembroEmpresa['rol_miembro'] })} className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                  {rolesDisponibles.map((rol) => <option key={rol.value} value={rol.value}>{rol.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="empresa-editar-miembro-estado" className="sr-only">Estado</label>
                <select id="empresa-editar-miembro-estado" name="miembroAEditar.estado" value={miembroAEditar.estado} onChange={(e) => setMiembroAEditar({ ...miembroAEditar, estado: e.target.value as MiembroEmpresa['estado'] })} className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="suspendido">Suspendido</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setMostrarModalEditarMiembro(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-600 dark:text-white">Cancelar</button>
              <button onClick={guardarEdicionMiembro} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">Guardar cambios</button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalGrupo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Nuevo equipo o área</h3>
              <button onClick={() => setMostrarModalGrupo(false)} aria-label="Cerrar modal de nuevo grupo" className="text-gray-500"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="empresa-nuevo-grupo-nombre" className="sr-only">Nombre</label>
                <input id="empresa-nuevo-grupo-nombre" name="nuevoGrupo.nombre" value={nuevoGrupo.nombre} onChange={(e) => setNuevoGrupo({ ...nuevoGrupo, nombre: e.target.value })} placeholder="Nombre" className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-grupo-descripcion" className="sr-only">Descripción</label>
                <textarea id="empresa-nuevo-grupo-descripcion" name="nuevoGrupo.descripcion" value={nuevoGrupo.descripcion} onChange={(e) => setNuevoGrupo({ ...nuevoGrupo, descripcion: e.target.value })} placeholder="Descripción" className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" rows={3} />
              </div>
              <div>
                <label htmlFor="empresa-nuevo-grupo-tipo" className="sr-only">Tipo de grupo</label>
                <select id="empresa-nuevo-grupo-tipo" name="nuevoGrupo.tipo_grupo" value={nuevoGrupo.tipo_grupo} onChange={(e) => setNuevoGrupo({ ...nuevoGrupo, tipo_grupo: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                  {tiposGrupo.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setMostrarModalGrupo(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-600 dark:text-white">Cancelar</button>
              <button onClick={crearGrupo} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">Crear grupo</button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalEditarGrupo && grupoAEditar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Editar grupo</h3>
              <button onClick={() => setMostrarModalEditarGrupo(false)} aria-label="Cerrar modal de edición de grupo" className="text-gray-500"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="empresa-editar-grupo-nombre" className="sr-only">Nombre</label>
                <input id="empresa-editar-grupo-nombre" name="grupoAEditar.nombre" value={grupoAEditar.nombre} onChange={(e) => setGrupoAEditar({ ...grupoAEditar, nombre: e.target.value })} placeholder="Nombre" className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
              </div>
              <div>
                <label htmlFor="empresa-editar-grupo-descripcion" className="sr-only">Descripción</label>
                <textarea id="empresa-editar-grupo-descripcion" name="grupoAEditar.descripcion" value={grupoAEditar.descripcion || ''} onChange={(e) => setGrupoAEditar({ ...grupoAEditar, descripcion: e.target.value })} placeholder="Descripción" className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white" rows={3} />
              </div>
              <div>
                <label htmlFor="empresa-editar-grupo-tipo" className="sr-only">Tipo de grupo</label>
                <select id="empresa-editar-grupo-tipo" name="grupoAEditar.tipo_grupo" value={grupoAEditar.tipo_grupo || 'equipo'} onChange={(e) => setGrupoAEditar({ ...grupoAEditar, tipo_grupo: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                  {tiposGrupo.map((tipo) => <option key={tipo.value} value={tipo.value}>{tipo.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setMostrarModalEditarGrupo(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-600 dark:text-white">Cancelar</button>
              <button onClick={guardarEdicionGrupo} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">Guardar cambios</button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalGestionGrupo && grupoSeleccionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gestionar {grupoSeleccionado.nombre}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Agrega o remueve miembros del grupo seleccionado.</p>
              </div>
              <button onClick={cerrarGestionGrupo} aria-label="Cerrar gestión de grupo" className="text-gray-500"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
              <div>
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <Users className="h-4 w-4" />
                  Miembros del grupo
                </h4>
                <div className="space-y-3">
                  {miembrosGrupo.map((miembro) => (
                    <div key={miembro.id_grupo_miembro} className="flex items-center justify-between rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{`${miembro.nombre} ${miembro.apellido || ''}`.trim()}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{miembro.email}</p>
                      </div>
                      <button onClick={() => quitarMiembroDeGrupo(miembro.id_miembro)} aria-label={`Quitar del grupo a ${miembro.nombre} ${miembro.apellido || ''}`.trim()} className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {miembrosGrupo.length === 0 && (
                    <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      Este grupo aún no tiene miembros.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Agregar miembro</h4>
                <div className="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                  <div>
                    <label htmlFor="empresa-gestion-grupo-busqueda" className="sr-only">Buscar disponible</label>
                    <input id="empresa-gestion-grupo-busqueda" name="busquedaGrupo" value={busquedaGrupo} onChange={(e) => setBusquedaGrupo(e.target.value)} placeholder="Buscar disponible" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <label htmlFor="empresa-gestion-grupo-miembro" className="sr-only">Selecciona un miembro</label>
                    <select id="empresa-gestion-grupo-miembro" name="miembroAAgregar" value={miembroAAgregar} onChange={(e) => setMiembroAAgregar(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                      <option value="">Selecciona un miembro</option>
                      {miembrosDisponiblesParaGrupo.map((miembro) => (
                        <option key={miembro.id_miembro} value={miembro.id_miembro}>
                          {`${miembro.nombre} ${miembro.apellido || ''}`.trim()} - {miembro.rol_miembro}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button onClick={agregarMiembroAGrupo} className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">
                    Agregar al grupo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaginaGestionEmpresa
